import {
  DIRECT_FEEDBACK_BODY_MAX,
  DIRECT_FEEDBACK_MAX_CORE_VALUES,
  DIRECT_FEEDBACK_MAX_FRAMEWORKS,
} from '@cult/domain';
import { z } from 'zod';
import {
  BaseEntitySchema,
  OffsetQuerySchema,
  createdRangeFields,
  englishText,
  englishTitleField,
  idField,
  idMultiField,
  qField,
  timeZoneField,
  updatedAtField,
} from './common';
import { PRAISE_TAG_REQUIRED, PraisePeriodSchema } from './praises';
import { ReferenceEmbedSchema, tagIdsField } from './reference';
import { PublicAttachmentSchema, attachmentsInputField } from './uploads';
import { UserSummarySchema } from './users';

const whatHappenedField = englishText(z.string().trim().min(1).max(DIRECT_FEEDBACK_BODY_MAX));
// Same rule as praises: each list may be empty, but new feedback needs at least one tag in total.
const coreValuesField = tagIdsField(DIRECT_FEEDBACK_MAX_CORE_VALUES, 'Core values');
const frameworksField = tagIdsField(DIRECT_FEEDBACK_MAX_FRAMEWORKS, 'Frameworks');
export const DIRECT_FEEDBACK_TAG_REQUIRED = PRAISE_TAG_REQUIRED;

export const DirectFeedbackCreateInputSchema = z
  .object({
    receiver: idField,
    coreValues: coreValuesField.default([]),
    frameworks: frameworksField.default([]),
    title: englishTitleField,
    whatHappened: whatHappenedField,
    attachments: attachmentsInputField,
  })
  .refine((d) => d.coreValues.length + d.frameworks.length > 0, {
    message: DIRECT_FEEDBACK_TAG_REQUIRED,
    path: ['coreValues'],
  });
export type DirectFeedbackCreateInput = z.infer<typeof DirectFeedbackCreateInputSchema>;

export const DirectFeedbackUpdateInputSchema = z
  .object({
    receiver: idField.optional(),
    coreValues: coreValuesField.optional(),
    frameworks: frameworksField.optional(),
    title: englishTitleField.optional(),
    whatHappened: whatHappenedField.optional(),
    attachments: attachmentsInputField,
  })
  .refine((d) => Object.keys(d).length > 0, { message: 'At least one field is required' });
export type DirectFeedbackUpdateInput = z.infer<typeof DirectFeedbackUpdateInputSchema>;

export const PublicDirectFeedbackSchema = BaseEntitySchema.extend({
  updatedAt: updatedAtField,
  number: z.number().int(),
  author: UserSummarySchema,
  receiver: UserSummarySchema,
  coreValues: z.array(ReferenceEmbedSchema),
  frameworks: z.array(ReferenceEmbedSchema),
  title: z.string(),
  // No max: legacy bodies merged from the four SBI+R sections can exceed DIRECT_FEEDBACK_BODY_MAX.
  whatHappened: z.string(),
  // Deprecated: kept only so a not-yet-redeployed web build keeps rendering. Remove with the legacy columns.
  situation: z.string().optional(),
  behavior: z.string().optional(),
  impact: z.string().optional(),
  request: z.string().optional(),
  attachments: z.array(PublicAttachmentSchema),
});
export type PublicDirectFeedback = z.infer<typeof PublicDirectFeedbackSchema>;

export const DirectFeedbackListQuerySchema = OffsetQuerySchema.extend({
  period: PraisePeriodSchema.optional(),
  author: idField.optional(),
  receiver: idField.optional(),
  coreValues: idMultiField,
  frameworks: idMultiField,
  q: qField,
  ...createdRangeFields,
});
export type DirectFeedbackListQuery = z.infer<typeof DirectFeedbackListQuerySchema>;

export const DirectFeedbackExportQuerySchema = DirectFeedbackListQuerySchema.omit({ offset: true, limit: true }).extend(
  {
    tz: timeZoneField,
  },
);
export type DirectFeedbackExportQuery = z.infer<typeof DirectFeedbackExportQuerySchema>;

export const DirectFeedbackListResponseSchema = z.object({
  feedback: z.array(PublicDirectFeedbackSchema),
  total: z.number().int().nonnegative(),
});
export type DirectFeedbackListResponse = z.infer<typeof DirectFeedbackListResponseSchema>;
